import { TreeView, NodeId, SharedTree } from '@fluid-experimental/tree';
import { logVerbose } from '../../log';
import { MfsItem, isMfsItem, MfsItemId, MfsQuery, isBaseProperty } from '../..';
import { throwIfInvalidMfsItem } from '../../errors';

import { EDGE_PREFIX } from '../../store/relations';
import { COLLECTION_KIND_KEY, LABEL_PROPERTY, Writable } from '../../utility';
import { getIndexedNodes, getTypedNode, tryGetItemNodeId } from './nodesRead';
import { getPropertyValue } from './propertiesRead';
import { isItemNode } from './typeGuards';

import { COLLECTION_VALUES, ITEMS } from './types';

/** Gets the typed-item with select properties. */
export function getItem<T extends MfsItem>(
  tree: SharedTree,
  view: TreeView,
  itemId: MfsItemId,
  typeGuard: (item: MfsItem, select?: string[]) => item is T,
  select?: string[]
): T | undefined {
  const itemNodeId = tryGetItemNodeId(tree, view, itemId);
  if (!itemNodeId) {
    return undefined;
  }

  return getItemByNodeId(view, itemNodeId, typeGuard, select);
}

function getItemByNodeId<T extends MfsItem>(
  view: TreeView,
  itemNodeId: NodeId,
  typeGuard: (item: MfsItem, select?: string[]) => item is T,
  select?: string[]
) {
  const mfsItem = readMfsItem(view, itemNodeId, select);
  return typeGuard(mfsItem, select) ? mfsItem : undefined;
}

/** Gets the full item with all properties added by all apps. */
export function getMfsItem(
  tree: SharedTree,
  view: TreeView,
  itemId: MfsItemId,
  select?: string[]
): MfsItem | undefined {
  const itemNodeId = tryGetItemNodeId(tree, view, itemId);
  if (!itemNodeId) {
    return undefined;
  }

  const mfsItem = readMfsItem(view, itemNodeId, select);
  return isMfsItem(mfsItem, select) ? mfsItem : undefined;
}

function readMfsItem(view: TreeView, itemNodeId: NodeId, selectedKeys?: string[]): MfsItem {
  const mfsItem: Writable<Partial<MfsItem>> = {};

  const node = getTypedNode(view, itemNodeId, isItemNode);

  mfsItem.id = node.payload.id;

  if (!selectedKeys || selectedKeys.includes(LABEL_PROPERTY)) {
    mfsItem.label = node.payload.label;
  }

  if ((!selectedKeys || selectedKeys.includes(COLLECTION_KIND_KEY)) && node.payload.kind) {
    mfsItem.kind = node.payload.kind;
  }

  for (const [key, nodeIds] of node.traits) {
    if (key.startsWith(EDGE_PREFIX)) {
      // skip, related items are not included in the item.
      logVerbose('readMfsItem', 'Related items are not included in the item, skipping');
      continue;
    }

    if (key === COLLECTION_VALUES) {
      logVerbose('readMfsItem', 'Collection values are not included in the item, skipping');
      // skip, collection values are not included in the item.
      continue;
    }

    const getValue = () => getPropertyValue(view, nodeIds);

    if (!selectedKeys || selectedKeys.includes(key)) {
      mfsItem[key] = getValue();
    }
  }

  throwIfInvalidMfsItem(mfsItem, selectedKeys);

  return mfsItem;
}

/** Gets the Mfs item node ids under the root ITEMS trait. */
export function getMfsItemNodeIds(view: TreeView): readonly NodeId[] {
  return getIndexedNodes(view, ITEMS);
}

/** A pretend-iterator to allow querying items in order of insertion time or reverse. */
function getOrderedNodeIds(view: TreeView, orderBy?: MfsQuery['orderBy']): IterableIterator<NodeId> {
  return orderBy === 'desc' ? getItemNodeIdsReverseIterator(view) : getItemNodeIdIterator(view);
}

function* getItemNodeIdsReverseIterator(view: TreeView): IterableIterator<NodeId> {
  const mfsItemIds = getMfsItemNodeIds(view);
  for (let i = mfsItemIds.length - 1; i >= 0; i--) {
    yield mfsItemIds[i];
  }
}

function getItemNodeIdIterator(view: TreeView): IterableIterator<NodeId> {
  return getMfsItemNodeIds(view).values();
}

/** @yields Items from the tree. */
export function* getItemsWithoutPaging<T extends MfsItem>(
  view: TreeView,
  typeGuard: (item: MfsItem, select?: string[]) => item is T,
  query?: MfsQuery
): IterableIterator<T> {
  for (const itemNodeId of getOrderedNodeIds(view, query?.orderBy)) {
    // TODO: (denizc) Refactor post-migration https://office.visualstudio.com/MetaOS/_boards/board/t/MFS/Backlog%20Items/?workitem=5766678
    // apply select after the filter, if only current app namespace is included
    const item = query?.filter
      ? getItemByNodeId(view, itemNodeId, typeGuard)
      : getItemByNodeId(view, itemNodeId, typeGuard, query?.select);

    if (item && (!query || !query?.filter || query.filter(item))) {
      const result = query?.select ? getItemByNodeId(view, itemNodeId, typeGuard, query.select)! : item;
      yield result;
    }
  }
}
